import { AntDesign } from '@expo/vector-icons';
import {
  EmailAuthProvider as EmailAuthProviderMobile,
  FirebaseAuthTypes,
  reauthenticateWithCredential
} from '@react-native-firebase/auth';
import { captureException } from '@sentry/react-native';
import { router, useNavigation } from 'expo-router';
import {
  EmailAuthProvider as EmailAuthProviderWeb,
  reauthenticateWithCredential as reauthenticateWithCredentialWeb,
  updatePassword,
  User
} from 'firebase/auth';
import { useLayoutEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import FormWrapper from '@/components/forms/FormWrapper';
import GuardWrapper from '@/components/GuardWrapper';
import TextInputField from '@/components/inputs/TextInputField';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { auth } from '@/firebaseConfig';
import { ResetPasswordData, resetPasswordResolver } from '@/lib/validations/ResetPasswordSchema';
import { i18n } from '@/utils/i18n';

const ChangePassword = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigation = useNavigation();
  const { showSuccess, showError } = useEndFlowModal();
  const { state } = useAuth();
  const user = auth().currentUser;

  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid }
  } = useForm<ResetPasswordData>({
    mode: 'onChange',
    resolver: resetPasswordResolver
  });
  const newPasswordWatch = watch('newPassword');

  //Validations
  const lengthValidation = newPasswordWatch && newPasswordWatch.length >= 8;
  const numbersValidation = newPasswordWatch && newPasswordWatch.match(/(?=.*[0-9])/);
  const lowerCaseValidation = newPasswordWatch && newPasswordWatch.match(/(?=.*[a-z])/);
  const upperCaseValidation = newPasswordWatch && newPasswordWatch.match(/(?=.*[A-Z])/);

  //Locales
  const title = i18n.t('settings.screen_names.change_password');
  const validations_title = i18n.t('settings.change_password.validations_title');
  const min_characters = i18n.t('settings.change_password.min_characters');
  const min_numbers = i18n.t('settings.change_password.min_numbers');
  const min_lowercase = i18n.t('settings.change_password.min_lowercase');
  const min_uppercase = i18n.t('settings.change_password.min_uppercase');
  const success = i18n.t('end_flow.success_title');
  const success_msg = i18n.t('settings.change_password.success_message');
  const failure = i18n.t('end_flow.error_title');
  const failure_msg = i18n.t('settings.change_password.failure_message');
  const failure_reauthenticate_message = i18n.t('settings.change_password.failure_reauthentication_message');
  const current_password_placeholder = i18n.t('settings.change_password.current_password_placeholder');
  const new_password_placeholder = i18n.t('settings.change_password.new_password_placeholder');
  const confirmation_placeholder = i18n.t('settings.change_password.confirmation_placeholder');
  const save = i18n.t('mix.save');

  const changePasswordSubmit: SubmitHandler<ResetPasswordData> = async (data) => {
    try {
      setIsLoading(true);

      if (Platform.OS === 'web') {
        const authCredential = EmailAuthProviderWeb.credential(state.user?.user?.email!, data.currentPassword);
        reauthenticateWithCredentialWeb(user as User, authCredential)
          .then(async (userCredential) => {
            await updatePassword(userCredential.user, data.newPassword)
              .then(() => {
                showSuccess({
                  title: success,
                  message: success_msg
                });
                router.back();
              })
              .catch((e) => {
                showError({
                  title: failure,
                  message: failure_msg
                });
              });
          })
          .catch(() => {
            showError({
              title: failure,
              message: failure_reauthenticate_message
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        const authCredential = EmailAuthProviderMobile.credential(state.user?.user?.email!, data.currentPassword);
        reauthenticateWithCredential(user as FirebaseAuthTypes.User, authCredential)
          .then(async (userCredential) => {
            await userCredential.user
              .updatePassword(data.newPassword)
              .then(() => {
                showSuccess({
                  title: success,
                  message: success_msg
                });
                router.back();
              })
              .catch((e) => {
                showError({
                  title: failure,
                  message: failure_msg
                });
              });
          })
          .catch(() => {
            showError({
              title: failure,
              message: failure_reauthenticate_message
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    } catch (error) {
      console.error(error);
      captureException(error);
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={title} />,
      headerLeft: () => <GoBackButton />
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  return (
    <GuardWrapper canAccess={['NON_PROFIT_ORG']}>
      <FormWrapper>
        <View style={styles.container}>
          <TextInputField
            name="currentPassword"
            control={control}
            placeholder={current_password_placeholder}
            type="password"
          />
          <TextInputField name="newPassword" control={control} placeholder={new_password_placeholder} type="password" />
          <TextInputField
            name="confirmPassword"
            control={control}
            placeholder={confirmation_placeholder}
            type="password"
          />
          <View style={styles.validationsContainer}>
            <Text style={[styles.validationsText, styles.validationsTitle]} fontWeight="450">
              {validations_title}
            </Text>
            <View style={styles.validationRow}>
              {lengthValidation ? (
                <AntDesign name="check-circle" size={24} color="black" />
              ) : (
                <View style={styles.circle} />
              )}
              <Text style={styles.validationsText} fontWeight="450">
                {min_characters}
              </Text>
            </View>
            <View style={styles.validationRow}>
              {numbersValidation ? (
                <AntDesign name="check-circle" size={24} color="black" />
              ) : (
                <View style={styles.circle} />
              )}
              <Text style={styles.validationsText} fontWeight="450">
                {min_numbers}
              </Text>
            </View>
            <View style={styles.validationRow}>
              {lowerCaseValidation ? (
                <AntDesign name="check-circle" size={24} color="black" />
              ) : (
                <View style={styles.circle} />
              )}
              <Text style={styles.validationsText} fontWeight="450">
                {min_lowercase}
              </Text>
            </View>
            <View style={styles.validationRow}>
              {upperCaseValidation ? (
                <AntDesign name="check-circle" size={24} color="black" />
              ) : (
                <View style={styles.circle} />
              )}
              <Text style={styles.validationsText} fontWeight="450">
                {min_uppercase}
              </Text>
            </View>
          </View>
          <BrandedButton onPress={handleSubmit(changePasswordSubmit)} disabled={!isValid} loading={isLoading}>
            <Text style={styles.btnText} fontWeight="700">
              {save}
            </Text>
          </BrandedButton>
        </View>
      </FormWrapper>
    </GuardWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    rowGap: 25,
    paddingVertical: 18
  },
  btnText: {
    color: 'white',
    fontSize: 14
  },
  validationsTitle: {
    marginBottom: 12
  },
  validationsContainer: {
    flexDirection: 'column',
    rowGap: 8
  },
  validationsText: {
    color: 'rgba(0,0,0,0.6)',
    fontSize: 14
  },
  validationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 15
  },
  circle: {
    borderRadius: 12,
    height: 24,
    width: 24,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center'
  }
});

export default ChangePassword;
